import { useEffect, useRef, useState } from 'react'

import { Box, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { F0Card, F0Heading, F0Text, IconType } from '@factorialco/f0-react'
import {
  CardSelectableContainer,
  CardSelectableItem,
} from '@factorialco/f0-react/dist/experimental'
import { Bank, Laptop, Present, Receipt, Wallet } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  BankingOnboardingFragment,
  BankingValueObjectsOnboardingCreationSourceValueEnum,
  LegalEntityForSelectFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { LegalEntitySelectorF0 } from 'modules/banking/components/LegalEntitySelectorF0'
import { WizardData } from 'modules/banking/components/NewAccountModal/types'
import { WizardStepLayout } from 'modules/banking/components/NewAccountModal/wizardSteps/WizardStepLayout'
import { defaultBankNumberFormat } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { useBankingAccountCreationAvailability } from 'modules/banking/pages/AccountsListPage/useBankingAccountCreationAvailability'
import { getDefaultAlias } from 'modules/banking/utils'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface UseCaseOption {
  value: BankingValueObjectsOnboardingCreationSourceValueEnum
  title: string
  description: string
  icon: IconType
  disabled: boolean
  disabledLabel?: string
  reason?: 'available' | 'needs_upsell' | 'no_permission' | 'already_active'
}

interface Props {
  existingOnboardings: BankingOnboardingFragment[]
}

interface UpsellModuleConfig {
  module: AvailableModules
  feature?: Features
}

const MODULE_MAP: Record<string, UpsellModuleConfig> = {
  expenses: { module: AvailableModules.expenses },
  software: { module: AvailableModules.it_management },
  benefits: { module: AvailableModules.benefits },
  payments: { module: AvailableModules.finance },
  finance: { module: AvailableModules.expenses, feature: Features.accounts_payable_management },
}

const CREATION_SOURCE_ICON_MAP: Record<string, IconType> = {
  expenses: Receipt,
  software: Laptop,
  payments: Wallet,
  benefits: Present,
  finance: Bank,
  accounts_payable: Bank,
}

interface UpsellCardProps {
  option: UseCaseOption
  config: UpsellModuleConfig
}

const UpsellCard = ({ option, config }: UpsellCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <F0Card
        title={option.title}
        description={option.description}
        avatar={{ type: 'icon', icon: option.icon }}
        onClick={() => setIsModalOpen(true)}
        compact
      />
      {isModalOpen && config.feature ? (
        <UpsellingKit.FeatureModal
          moduleRequested={config.module}
          featureRequested={config.feature}
          requestSource="Banking account creation wizard"
          requestReason={ValidReasons.MissingAddon}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      ) : isModalOpen ? (
        <UpsellingKit.ProductModal
          moduleRequested={config.module}
          requestSource="Banking account creation wizard"
          requestReason={ValidReasons.MissingAddon}
          pageSource="Banking account creation wizard"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}
    </>
  )
}

export const BankAccountTypeSelectorWizardStepF0 = ({
  data,
  onDataChanged,
  existingOnboardings,
}: WizardStepRenderParams<WizardData> & Props) => {
  const { loading, options } = useBankingAccountCreationAvailability()
  const { canRequestUpsell } = useCurrentSession()

  // Use ref to avoid infinite loops from callback recreations
  const dataRef = useRef(data)
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const handleLegalEntityChanged = useUnsafeCallback(
    (newLegalEntity: LegalEntityForSelectFragment | null) => {
      const currentData = dataRef.current
      onDataChanged({
        ...currentData,
        legalEntity: newLegalEntity,
        currency: newLegalEntity?.currency || currentData.currency,
        accountNumberType: newLegalEntity?.country
          ? defaultBankNumberFormat(newLegalEntity.country)
          : currentData.accountNumberType,
        country: newLegalEntity?.country.toLocaleUpperCase() ?? currentData.country,
        // Reset creation source when legal entity changes
        creationSource: null,
      })
    },
    [onDataChanged]
  )

  const existingCreationSources = useUnsafeMemo(() => {
    const legalEntity = data.legalEntity
    if (!legalEntity) return new Set<string>()

    return new Set(
      existingOnboardings
        .filter((onboarding) => onboarding.legalEntity?.id === legalEntity.id)
        .map((onboarding) => onboarding.creationSource)
        .filter(Boolean)
    )
  }, [existingOnboardings, data.legalEntity])

  const allUseCaseOptions: UseCaseOption[] = useUnsafeMemo(() => {
    const hasExpenses = existingCreationSources.has('expenses')
    const hasSoftware = existingCreationSources.has('software')
    const hasPayments = existingCreationSources.has('payments')
    const hasBenefits = existingCreationSources.has('benefits')
    // Note: hasFinance not needed - GoCardless can have multiple accounts per legal entity

    const toEnum = (
      value: 'expenses' | 'software' | 'payments' | 'benefits' | 'finance'
    ): BankingValueObjectsOnboardingCreationSourceValueEnum => {
      return value
    }

    return [
      {
        value: toEnum('expenses'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.expenses.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.expenses.description'),
        icon: Receipt,
        disabled: hasExpenses || !options.expenses.canCreate,
        disabledLabel: hasExpenses
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.expenses.already_active')
          : options.expenses.reason === 'needs_upsell'
            ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
            : undefined,
        reason: hasExpenses ? 'already_active' : options.expenses.reason,
      },
      {
        value: toEnum('software'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.software.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.software.description'),
        icon: Laptop,
        disabled: hasSoftware || !options.software.canCreate,
        disabledLabel: hasSoftware
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.software.already_active')
          : options.software.reason === 'needs_upsell'
            ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
            : undefined,
        reason: hasSoftware ? 'already_active' : options.software.reason,
      },
      {
        value: toEnum('payments'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.payments.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.payments.description'),
        icon: Wallet,
        disabled: hasPayments || !options.payments.canCreate,
        disabledLabel: hasPayments
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.payments.already_active')
          : options.payments.reason === 'needs_upsell'
            ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
            : undefined,
        reason: hasPayments ? 'already_active' : options.payments.reason,
      },
      {
        value: toEnum('benefits'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.benefits.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.benefits.description'),
        icon: Present,
        disabled: hasBenefits || !options.benefits.canCreate,
        disabledLabel: hasBenefits
          ? i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.benefits.already_active')
          : options.benefits.reason === 'needs_upsell'
            ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
            : undefined,
        reason: hasBenefits ? 'already_active' : options.benefits.reason,
      },
      {
        // GoCardless (finance) can have multiple accounts per legal entity - no "already_active" restriction
        value: toEnum('finance'),
        title: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.finance.title'),
        description: i18n.t('banking.accounts.new_wizard.step_1_1.use_cases.finance.description'),
        icon: Bank,
        disabled: !options.accountsPayable.canCreate,
        disabledLabel:
          options.accountsPayable.reason === 'needs_upsell'
            ? i18n.t('banking.accounts.new_wizard.step_1_1.module_not_purchased')
            : undefined,
        reason: options.accountsPayable.reason,
      },
    ]
  }, [existingCreationSources, options])

  const availableOptions = useUnsafeMemo(
    () => allUseCaseOptions.filter((option) => option.reason === 'available'),
    [allUseCaseOptions]
  )

  const cardSelectableItems: CardSelectableItem<BankingValueObjectsOnboardingCreationSourceValueEnum>[] =
    useUnsafeMemo(
      () =>
        availableOptions.map((option) => ({
          value: option.value,
          title: option.title,
          description: option.description,
          avatar: { type: 'icon' as const, icon: option.icon },
          disabled: option.disabled,
        })),
      [availableOptions]
    )

  const disabledOptionsWithUpsell = useUnsafeMemo(
    () => allUseCaseOptions.filter((option) => option.reason === 'needs_upsell'),
    [allUseCaseOptions]
  )

  const existingAccounts = useUnsafeMemo(() => {
    const legalEntity = data.legalEntity
    if (!legalEntity) return []

    return existingOnboardings.filter((onboarding) => onboarding.legalEntity?.id === legalEntity.id)
  }, [existingOnboardings, data.legalEntity])

  const handleSelectUseCase = useUnsafeCallback(
    (value: BankingValueObjectsOnboardingCreationSourceValueEnum | undefined) => {
      if (value) {
        onDataChanged({
          ...dataRef.current,
          creationSource: value,
        })
      }
    },
    [onDataChanged]
  )

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.new_wizard.step_1.title')}
      subtitle={i18n.t('banking.accounts.new_wizard.step_1.subtitle')}
    >
      <Box gap="s24" style={{ maxWidth: '600px' }}>
        <LegalEntitySelectorF0
          onChange={handleLegalEntityChanged}
          value={data.legalEntity}
          required
        />

        {/* Purpose section - only show when legal entity is selected and data is loaded */}
        {data.legalEntity && !loading && (
          <Box gap="s16" flexDirection="column">
            <Box gap="s4" flexDirection="column">
              <F0Heading content={i18n.t('banking.accounts.new_wizard.step_1_1.title')} />
              <F0Text
                variant="description"
                content={i18n.t('banking.accounts.new_wizard.step_1_1.subtitle')}
              />
            </Box>
            {cardSelectableItems.length > 0 ? (
              <CardSelectableContainer
                items={cardSelectableItems}
                value={data.creationSource ?? undefined}
                onChange={handleSelectUseCase}
                label={i18n.t('banking.accounts.new_wizard.step_1_1.subtitle')}
              />
            ) : (
              <Box padding="s16" background="grey100" borderRadius={{ all: 'abs008' }}>
                <F0Text
                  variant="description"
                  content={i18n.t('banking.accounts.new_wizard.step_1_1.no_available_options')}
                />
              </Box>
            )}

            {disabledOptionsWithUpsell.length > 0 && canRequestUpsell && (
              <Box gap="s8" flexDirection="column">
                <F0Text
                  variant="label"
                  content={i18n.t('banking.accounts.new_wizard.step_1_1.upgrade_required')}
                />
                {disabledOptionsWithUpsell
                  .map((option) => {
                    const config = MODULE_MAP[option.value]
                    if (!config) return null
                    return <UpsellCard key={option.value} option={option} config={config} />
                  })
                  .filter(Boolean)}
              </Box>
            )}

            {existingAccounts.length > 0 && (
              <Box gap="s8" flexDirection="column">
                <F0Text
                  variant="label"
                  content={i18n.t('banking.accounts.new_wizard.step_1_1.existing_accounts')}
                />
                <Box gap="s8" flexDirection="column">
                  {existingAccounts.map((onboarding) => (
                    <F0Card
                      key={onboarding.id}
                      title={getDefaultAlias(onboarding.creationSource) ?? ''}
                      description={onboarding.legalEntity.legalName}
                      avatar={{
                        type: 'icon',
                        icon: CREATION_SOURCE_ICON_MAP[onboarding.creationSource] ?? Bank,
                      }}
                      compact
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </WizardStepLayout>
  )
}
